import { describeError } from '@/lib/form/errorMessages';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Plus, Pencil, Trash2, Search, Mail, Phone, Building2, MapPin } from 'lucide-react';
import { Permission, type ClientListItem } from '@kreart/shared';
import { Button } from '@/components/ui/button';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { EmptyState, ErrorState, CardSkeleton, TableRowSkeleton } from '@/components/feedback';
import { useAuth } from '@/features/auth/useAuth';
import { useClients, useDeleteClient } from '@/features/clients/hooks';
import { toListQuery, type ClientsListSearch } from '@/features/clients/search';
import { errorKeyFor } from '@/features/clients/errorMessages';
import { ConfirmDialog } from '@/features/users/components/ConfirmDialog';
import { clickableRowProps } from '@/lib/clickableRow';
import { RowCard } from '@/components/RowCard';
import { ColumnOrderMenu } from '@/components/table/ColumnOrderMenu';
import { usePersistedColumnOrder } from '@/components/table/usePersistedColumnOrder';

interface ClientsTableColumn {
  key: string;
  label: string;
  className?: string;
  render: (client: ClientListItem) => ReactNode;
}

export function ClientsListPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as ClientsListSearch;

  const can = (p: string) => user?.permissions.includes(p) ?? false;
  const canCreate = can(Permission.CLIENTS_CREATE);
  const canUpdate = can(Permission.CLIENTS_UPDATE);
  const canDelete = can(Permission.CLIENTS_DELETE);

  const query = useMemo(() => toListQuery(search), [search]);
  const { data, isLoading, isError, error, isPlaceholderData, refetch } = useClients(query);

  const [searchInput, setSearchInput] = useState(search.search ?? '');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => setSearchInput(search.search ?? ''), [search.search]);

  const updateSearch = (patch: Partial<ClientsListSearch>, opts: { replace?: boolean } = {}) => {
    const next: ClientsListSearch = { ...search, ...patch };
    const clean: ClientsListSearch = {};
    if (next.page && next.page !== 1) clean.page = next.page;
    if (next.pageSize && next.pageSize !== 25) clean.pageSize = next.pageSize;
    if (next.search) clean.search = next.search;
    if (next.includeDeleted) clean.includeDeleted = true;
    void navigate({ to: '/clients', search: clean, replace: opts.replace });
  };

  const onSearchChange = (value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const trimmed = value.trim();
      updateSearch({ search: trimmed || undefined, page: 1 }, { replace: true });
    }, 300);
  };

  const [deleteTarget, setDeleteTarget] = useState<ClientListItem | null>(null);
  const deleteMut = useDeleteClient(deleteTarget?.id ?? '');

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMut.mutateAsync();
      setDeleteTarget(null);
      deleteMut.reset();
    } catch {
      /* error surfaced via deleteMut.error */
    }
  };

  const currentPage = search.page ?? 1;
  const pageSize = search.pageSize ?? 25;
  const total = data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const tableColumns: ClientsTableColumn[] = [
    {
      key: 'name',
      label: t('clients.columns.name'),
      render: (c) => {
        const isDeleted = c.deletedAt !== null;
        return (
          <div className="flex items-center gap-2">
            <span className="font-medium">{c.name}</span>
            {isDeleted && (
              <span className="rounded-md bg-destructive/10 px-2 py-0.5 text-xs text-destructive">
                {t('clients.badges.deleted')}
              </span>
            )}
            {c.contactName && (
              <span className="text-xs text-muted-foreground">· {c.contactName}</span>
            )}
          </div>
        );
      },
    },
    {
      key: 'email',
      label: t('clients.columns.email'),
      render: (c) =>
        c.email ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Mail className="h-4 w-4" />
            <span>{c.email}</span>
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'phone',
      label: t('clients.columns.phone'),
      render: (c) =>
        c.phone ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Phone className="h-4 w-4" />
            <span>{c.phone}</span>
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'company',
      label: t('clients.columns.company'),
      render: (c) =>
        c.company ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Building2 className="h-4 w-4" />
            <span>{c.company}</span>
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'city',
      label: t('clients.columns.city'),
      render: (c) =>
        c.city ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <MapPin className="h-4 w-4" />
            <span>{c.city}</span>
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'projects',
      label: t('clients.columns.projects'),
      className: 'text-right',
      render: (c) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            void navigate({ to: '/projects', search: { clientId: c.id } });
          }}
          className="inline-flex items-center rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary hover:bg-primary/20"
        >
          {t('clients.columns.projectsCount', { count: c.projectCount })}
        </button>
      ),
    },
    {
      key: 'actions',
      label: t('clients.columns.actions'),
      className: 'text-right',
      render: (c) => {
        const isDeleted = c.deletedAt !== null;
        const editable = canUpdate && !isDeleted;
        return (
          <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            {editable && (
              <Link to="/clients/$id" params={{ id: c.id }}>
                <Button variant="ghost" aria-label={t('clients.actions.edit') ?? 'edit'}>
                  <Pencil className="h-4 w-4" />
                </Button>
              </Link>
            )}
            {canDelete && !isDeleted && (
              <Button
                variant="ghost"
                onClick={() => setDeleteTarget(c)}
                aria-label={t('clients.actions.delete') ?? 'delete'}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        );
      },
    },
  ];
  const { orderedColumns, moveColumn, resetColumnOrder } = usePersistedColumnOrder(
    'clients.list',
    tableColumns,
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold">{t('clients.title')}</h1>
        {canCreate && (
          <Link to="/clients/new">
            <Button className="w-full sm:w-auto">
              <Plus className="mr-1.5 h-4 w-4" />
              {t('clients.new')}
            </Button>
          </Link>
        )}
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          value={searchInput}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t('clients.filters.searchPlaceholder') ?? ''}
          className="w-full rounded-md border border-border bg-background py-2 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {isError && <ErrorState error={error} onRetry={() => void refetch()} />}
      {deleteMut.isError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          {describeError(t, deleteMut.error, errorKeyFor)}
        </div>
      )}

      <div
        className={
          'hidden md:block overflow-hidden rounded-lg border border-border bg-card ' +
          (isPlaceholderData ? 'opacity-70 transition-opacity' : '')
        }
      >
        <div className="mb-2 flex justify-end">
          <ColumnOrderMenu
            columns={orderedColumns}
            onMoveColumn={moveColumn}
            onReset={resetColumnOrder}
          />
        </div>
        <Table>
          <THead>
            <TR>
              {orderedColumns.map((column) => (
                <TH key={column.key} className={column.className}>
                  {column.label}
                </TH>
              ))}
            </TR>
          </THead>
          {isLoading ? <TableRowSkeleton columns={orderedColumns.length} /> : null}
          <TBody>
            {!isLoading && data && data.items.length === 0 && (
              <TR>
                <TD colSpan={orderedColumns.length} className="py-6">
                  <EmptyState
                    description={t('clients.empty')}
                    action={
                      canCreate ? (
                        <Link to="/clients/new">
                          <Button className="h-8 px-3 py-1 text-xs">
                            <Plus className="mr-1.5 h-4 w-4" />
                            {t('clients.new')}
                          </Button>
                        </Link>
                      ) : undefined
                    }
                  />
                </TD>
              </TR>
            )}
            {data?.items.map((c) => {
              const isDeleted = c.deletedAt !== null;
              const editable = canUpdate && !isDeleted;
              const rowProps = clickableRowProps(
                editable ? () => navigate({ to: '/clients/$id', params: { id: c.id } }) : null,
              );
              return (
                <TR
                  key={c.id}
                  {...rowProps}
                  className={editable ? 'cursor-pointer hover:bg-muted/30 transition-colors' : ''}
                >
                  {orderedColumns.map((column) => (
                    <TD key={column.key} className={column.className}>
                      {column.render(c)}
                    </TD>
                  ))}
                </TR>
              );
            })}
          </TBody>
        </Table>
      </div>

      {/* Mobile card list */}
      <div
        className={
          'md:hidden space-y-2 ' + (isPlaceholderData ? 'opacity-70 transition-opacity' : '')
        }
      >
        {isLoading ? (
          <CardSkeleton count={4} className="grid-cols-1" />
        ) : data && data.items.length === 0 ? (
          <div className="rounded-lg border border-border bg-card py-6">
            <EmptyState
              description={t('clients.empty')}
              action={
                canCreate ? (
                  <Link to="/clients/new">
                    <Button className="h-8 px-3 py-1 text-xs">
                      <Plus className="mr-1.5 h-4 w-4" />
                      {t('clients.new')}
                    </Button>
                  </Link>
                ) : undefined
              }
            />
          </div>
        ) : (
          data?.items.map((c) => {
            const isDeleted = c.deletedAt !== null;
            const editable = canUpdate && !isDeleted;
            return (
              <RowCard
                key={c.id}
                onActivate={
                  editable ? () => navigate({ to: '/clients/$id', params: { id: c.id } }) : null
                }
                title={c.name}
                badges={
                  <>
                    {isDeleted && (
                      <span className="rounded-md bg-destructive/10 px-2 py-0.5 text-xs text-destructive">
                        {t('clients.badges.deleted')}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        void navigate({ to: '/projects', search: { clientId: c.id } });
                      }}
                      className="inline-flex items-center rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary hover:bg-primary/20"
                    >
                      {t('clients.columns.projectsCount', { count: c.projectCount })}
                    </button>
                  </>
                }
                subtitle={c.contactName ?? undefined}
                meta={
                  <>
                    {c.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="h-3.5 w-3.5" />
                        <span className="break-all">{c.email}</span>
                      </div>
                    )}
                    {c.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-3.5 w-3.5" />
                        <span>{c.phone}</span>
                      </div>
                    )}
                    {c.company && (
                      <div className="flex items-center gap-2">
                        <Building2 className="h-3.5 w-3.5" />
                        <span>{c.company}</span>
                      </div>
                    )}
                    {c.city && (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-3.5 w-3.5" />
                        <span>{c.city}</span>
                      </div>
                    )}
                  </>
                }
                actions={
                  <>
                    {editable && (
                      <Link to="/clients/$id" params={{ id: c.id }}>
                        <Button variant="ghost" aria-label={t('clients.actions.edit') ?? 'edit'}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </Link>
                    )}
                    {canDelete && !isDeleted && (
                      <Button
                        variant="ghost"
                        onClick={() => setDeleteTarget(c)}
                        aria-label={t('clients.actions.delete') ?? 'delete'}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </>
                }
              />
            );
          })
        )}
      </div>

      <div className="flex flex-col gap-2 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
        <div>
          {t('clients.pagination.range', {
            from: total === 0 ? 0 : (currentPage - 1) * pageSize + 1,
            to: Math.min(currentPage * pageSize, total),
            total,
          })}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            disabled={currentPage <= 1}
            onClick={() => updateSearch({ page: currentPage - 1 })}
          >
            {t('clients.pagination.prev')}
          </Button>
          <Button
            variant="outline"
            disabled={currentPage >= lastPage}
            onClick={() => updateSearch({ page: currentPage + 1 })}
          >
            {t('clients.pagination.next')}
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={deleteTarget !== null}
        title={t('clients.danger.confirmTitle')}
        description={t('clients.danger.confirmDescription', { name: deleteTarget?.name ?? '' })}
        confirmLabel={t('clients.danger.deleteButton')}
        variant="destructive"
        loading={deleteMut.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
